import { reorder as platformReorder } from '@atlaskit/pragmatic-drag-and-drop/reorder';

import { type MenuItemReorderOperation, OPERATIONS } from '../../utils/dnd-data';

export const reorderNestedItems = (
	operation: MenuItemReorderOperation,
	list: string[],
	itemId: string,
	relativeItemId?: string,
): string[] => {
	const currentIndex = list.indexOf(itemId);

	if (currentIndex === -1) {
		// Item not found, return original list
		return list;
	}

	let targetIndex: number;

	switch (operation) {
		case OPERATIONS.MOVE_TO_TOP:
			targetIndex = 0;
			break;
		case OPERATIONS.MOVE_UP:
			targetIndex = Math.max(0, currentIndex - 1);
			break;
		case OPERATIONS.MOVE_DOWN:
			targetIndex = Math.min(list.length - 1, currentIndex + 1);
			break;
		case OPERATIONS.MOVE_TO_BOTTOM:
			targetIndex = list.length - 1;
			break;
		case OPERATIONS.BEFORE:
			if (!relativeItemId) {
				return list;
			}
			// If trying to move item before itself, no change needed
			if (itemId === relativeItemId) {
				return list;
			}
			targetIndex = list.indexOf(relativeItemId);
			if (targetIndex === -1) {
				return list;
			}
			// If item is moving from after the target to before it
			if (currentIndex > targetIndex) {
				// Moving backward, insert before the target (no adjustment needed)
				break;
			} else {
				// Moving forward, adjust target index to account for removal
				targetIndex = Math.max(0, targetIndex - 1);
			}
			break;
		case OPERATIONS.AFTER:
			if (!relativeItemId) {
				return list;
			}
			// If trying to move item after itself, no change needed
			if (itemId === relativeItemId) {
				return list;
			}
			targetIndex = list.indexOf(relativeItemId);
			if (targetIndex === -1) {
				return list;
			}
			// If item is moving from before the target to after it
			if (currentIndex < targetIndex) {
				// Moving forward, target index stays the same (will be after due to removal)
				break;
			} else {
				// Moving backward, target becomes after the relative item
				targetIndex = Math.min(list.length - 1, targetIndex + 1);
			}
			break;
		default:
			return list;
	}

	// Use the platform's reorder utility
	return platformReorder({
		list,
		startIndex: currentIndex,
		finishIndex: targetIndex,
	});
};
